import React, { useState, useRef, useEffect, useCallback } from "react";
import { Drawer } from "../../../../lib/polygon-drawer";
import { DrawingControls } from "../../../../lib/polygon-drawer/DrawingControls";
import ParametersForm from "../../../../lib/polygon-drawer/ParametersForm";
import { motion } from "framer-motion";
import { FaDrawPolygon, FaUndo, FaRedo } from "react-icons/fa";
import Select from "react-select";
import { Tooltip } from "../../../../components/tooltip";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import {
  getRequest,
  optimisticOptions,
  postRequest,
  putRequest,
  queries,
  queryClient,
} from "../../../../react-query";
import { toast } from "react-toastify";
import getStorage from "../../../../services/storageService";

interface Point {
  x: number;
  y: number;
}

interface Zone {
  name: string;
  points: Point[];
}

interface HistoryState {
  zones: Zone[];
  activityParameters: Record<string, any>;
  drawingPoints?: Point[];
}

export interface ActivityOption {
  value: any;
  label: string;
}

interface SavedActivity {
  id: string;
  name: string;
  zones: Zone[];
  parameters: Record<string, any>;
  timestamp: string;
}


interface ActivityData {
  zones: {
    [key: string]: number[][];
  };
  parameters: any;
}

export const UpdateZone = () => {
  const navigate = useNavigate();
  const { uin_id } = useParams();
  const {
    state: { data },
  } = useLocation();
  const [activityData, setActivityData] = useState<any>();
  const [zones, setZones] = useState<Zone[]>([]);
  const [editingZoneIndex, setEditingZoneIndex] = useState<number | null>(null);
  const [showControls, setShowControls] = useState(false);
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [newZoneName, setNewZoneName] = useState("");
  const [activityParameters, setActivityParameters] = useState<
    Record<string, any>
  >({});
  const [savedActivities, setSavedActivities] = useState<SavedActivity[]>([]);
  const [editingActivityId, setEditingActivityId] = useState<string | null>(
    null
  );

  // Add history states
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const [activitySaved, setActivitySaved] = useState(false);
  const [showZonesSection, setShowZonesSection] = useState(true);
  const [highlightDrawingArea, setHighlightDrawingArea] = useState(false);
  const [highlightParameters, setHighlightParameters] = useState(false);
  const [hoveredZoneIndex, setHoveredZoneIndex] = useState<number | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isSnapshotLoading, setIsSnapshotLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  // Reference for the controls container
  const controlsRef = useRef<HTMLDivElement>(null);
  const controlButtonRef = useRef<HTMLButtonElement>(null);
  const drawingAreaRef = useRef<HTMLDivElement>(null);
  const parametersRef = useRef<HTMLDivElement>(null);

  // Drawing control states
  const [strokeColor, setStrokeColor] = useState("#2563eb");
  const [pointRadius, setPointRadius] = useState(4);
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [fillOpacity, setFillOpacity] = useState(0.2);

  // Activity selection state (lifted from Zone)
  const [selectedActivity, setSelectedActivity] =
    React.useState<ActivityOption | null>(null);

  const [drawingPoints, setDrawingPoints] = useState<Point[]>([]);

  const [imageWidth, setImageWidth] = useState<number | null>(null);
  const [imageHeight, setImageHeight] = useState<number | null>(null);

  const [undoRedoEnabled, setUndoRedoEnabled] = useState(true);
  const client_id = getStorage('client_id');

  const { data: configData = {}, isLoading: isConfigLoading }: any = useQuery(
    [queries.config, uin_id],
    () => getRequest(`/config/${uin_id}?client_id=${client_id}`),
    {
      enabled: !!uin_id,
    }
  );
  const { data: config = {} } = configData;



  const onActivityChange = (option: ActivityOption | null) => {
    setSelectedActivity(option);
  };
  const clearSelectedActivity = () => {
    setSelectedActivity(null);
  };

  // Handle click outside to close controls
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if controls are shown and if the click was outside the controls and button
      if (showControls) {
        // Get the DOM element from the motion.div
        const controlsElement = controlsRef.current;

        if (
          controlsElement &&
          controlButtonRef.current &&
          !controlsElement.contains(event.target as Node) &&
          !controlButtonRef.current.contains(event.target as Node)
        ) {
          setShowControls(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showControls]);

  // Initialize history when activity is selected or loaded
  useEffect(() => {
    if (selectedActivity) {
      // When a new activity is selected, reset history to initial state
      setHistory([
        {
          zones: [],
          activityParameters: selectedActivity.value || {},
        },
      ]);
      setHistoryIndex(0);
    } else {
      setHistory([]);
      setHistoryIndex(-1);
    }
  }, [selectedActivity]);

  // Function to add current state to history
  const addToHistory = useCallback((newZones: Zone[], newParameters: Record<string, any>, drawingPts?: Point[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({
      zones: newZones,
      activityParameters: newParameters,
      drawingPoints: drawingPts !== undefined ? drawingPts : drawingPoints,
    });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex, drawingPoints]);

  // Function to handle undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      setZones(previousState.zones);
      setActivityParameters(previousState.activityParameters);
      setDrawingPoints(previousState.drawingPoints || []);
      setHistoryIndex(newIndex);
      if (editingZoneIndex !== null) {
        const pts = previousState.zones[editingZoneIndex]?.points || [];
        lastPointsRef.current = pts.map(pt => ({ ...pt }));
      } else {
        lastPointsRef.current = previousState.drawingPoints ? previousState.drawingPoints.map(pt => ({ ...pt })) : null;
      }
    }
  }, [history, historyIndex, editingZoneIndex]);

  // Function to handle redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setZones(nextState.zones);
      setActivityParameters(nextState.activityParameters);
      setDrawingPoints(nextState.drawingPoints || []);
      setHistoryIndex(newIndex);
      if (editingZoneIndex !== null) {
        const pts = nextState.zones[editingZoneIndex]?.points || [];
        lastPointsRef.current = pts.map(pt => ({ ...pt }));
      } else {
        lastPointsRef.current = nextState.drawingPoints ? nextState.drawingPoints.map(pt => ({ ...pt })) : null;
      }
    }
  }, [history, historyIndex, editingZoneIndex]);

  const handleActivityChange = (option: ActivityOption | null) => {
    onActivityChange(option);
    if (option) {
      setActivityParameters(option.value);
      setShowZonesSection(true);
      // Reset history to initial state for new activity
      setHistory([
        {
          zones: [],
          activityParameters: option.value || {},
        },
      ]);
      setHistoryIndex(0);
    } else {
      setActivityParameters({});
      setHistory([]);
      setHistoryIndex(-1);
    }
    // Reset zones when activity changes
    setZones([]);
    setEditingZoneIndex(null);
    setIsDrawingEnabled(false);
    setActivitySaved(false);
    setEditingActivityId(null);
  };

  const handleParametersChange = (newParameters: Record<string, any>) => {
    setActivityParameters(newParameters);
    addToHistory(zones, newParameters, drawingPoints);
  };

  const lastPointsRef = useRef<Point[] | null>(null);

  const handleEditZone = (index: number) => {
    setIsDrawingEnabled(true); // Enable drawing when editing
    setEditingZoneIndex(index);
    lastPointsRef.current = null; // Reset lastPointsRef when editing starts
    setUndoRedoEnabled(true); // Enable Undo/Redo when editing starts
    scrollToDrawingArea();
  };

  const handleDrawingPointsChange = (points: Point[]) => {
    setDrawingPoints(points);
    // Only add to history if points actually changed
    const lastPoints = lastPointsRef.current;
    const pointsChanged =
      !lastPoints ||
      lastPoints.length !== points.length ||
      lastPoints.some((pt, i) => pt.x !== points[i].x || pt.y !== points[i].y);
    if (pointsChanged) {
      addToHistory(zones, activityParameters, points);
      lastPointsRef.current = points.map(pt => ({ ...pt }));
    }
  };

  const handlePolygonUpdate = (points: Point[]) => {
    if (editingZoneIndex !== null) {
      // Only add to history if points actually changed
      const lastPoints = lastPointsRef.current;
      const pointsChanged =
        !lastPoints ||
        lastPoints.length !== points.length ||
        lastPoints.some((pt, i) => pt.x !== points[i].x || pt.y !== points[i].y);

      if (pointsChanged) {
        const newZones = [...zones];
        newZones[editingZoneIndex] = {
          ...newZones[editingZoneIndex],
          points: points,
        };
        setZones(newZones);
        addToHistory(newZones, activityParameters, drawingPoints);
        lastPointsRef.current = points.map(pt => ({ ...pt }));
      }
      // Don't reset editingZoneIndex or isDrawingEnabled here
    }
  };

  const handlePolygonComplete = (points: Point[]) => {
    if (editingZoneIndex !== null) {
      // Update existing zone points and finish editing
      const newZones = [...zones];
      newZones[editingZoneIndex] = {
        ...newZones[editingZoneIndex],
        points: points,
      };
      setZones(newZones);
      addToHistory(newZones, activityParameters, drawingPoints);
      setEditingZoneIndex(null); // Auto-finish editing when polygon is complete
      setIsDrawingEnabled(false); // Disable drawing after completion
      lastPointsRef.current = null; // Reset lastPointsRef when editing ends
      setDrawingPoints([]); // Clear drawing points
      setUndoRedoEnabled(false); // Disable Undo/Redo after drawing complete
    } else {
      // Create new zone
      const newZones = [...zones, { name: newZoneName, points }];
      setZones(newZones);
      addToHistory(newZones, activityParameters, drawingPoints);
      setIsDrawingEnabled(false); // Disable drawing after zone completion
      setNewZoneName(""); // Reset zone name
      lastPointsRef.current = null; // Reset lastPointsRef when editing ends
      setDrawingPoints([]); // Clear drawing points
      setUndoRedoEnabled(false); // Disable Undo/Redo after drawing complete
    }
  };

  const scrollToDrawingArea = () => {
    // Scroll to drawing area
    setTimeout(() => {
      drawingAreaRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // Add highlight effect
      setHighlightDrawingArea(true);
      setTimeout(() => {
        setHighlightDrawingArea(false);
      }, 1500);
    }, 100);
  };

  const scrollToParametersArea = () => {
    // Scroll to parameters area
    setTimeout(() => {
      parametersRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // Add highlight effect to parameters section
      setHighlightParameters(true);
      setTimeout(() => {
        setHighlightParameters(false);
      }, 1500);
    }, 100);
  };

  const handleDeleteZone = (index: number) => {
    const newZones = zones.filter((_, i) => i !== index);
    setZones(newZones);
    addToHistory(newZones, activityParameters, drawingPoints);
    if (editingZoneIndex === index) {
      setEditingZoneIndex(null);
    }
  };

  const toggleControls = () => {
    setShowControls(!showControls);
  };

  const handleNewZoneClick = () => {
    if (!selectedActivity) {
      alert("Please select an activity first");
      return;
    }
    setShowNameDialog(true);
  };

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newZoneName.trim()) {
      setShowNameDialog(false);
      setIsDrawingEnabled(true);
      setEditingZoneIndex(null);
    }
  };

  const handleSaveActivity = () => {
    if (!selectedActivity || zones.length === 0) {
      toast.error(
        "Please select an activity and create at least one zone before saving"
      );
      return;
    }

    if (editingActivityId) {
      // Update existing activity
      setSavedActivities((prev) =>
        prev.map((activity) =>
          activity.id === editingActivityId
            ? {
                ...activity,
                name: selectedActivity.label,
                zones: [...zones],
                parameters: { ...activityParameters },
                timestamp: new Date().toLocaleString(),
              }
            : activity
        )
      );
    } else {
      // Create new activity
      const newSavedActivity: SavedActivity = {
        id: Date.now().toString(),
        name: selectedActivity.label,
        zones: [...zones],
        parameters: { ...activityParameters },
        timestamp: new Date().toLocaleString(),
      };
      setSavedActivities([...savedActivities, newSavedActivity]);
    }
    // Reset the current activity state
    setZones([]);
    setActivityParameters({});
    setIsDrawingEnabled(false);
    setEditingActivityId(null);
    setActivitySaved(true);
    setShowZonesSection(false);
    clearSelectedActivity(); // clear the selected activity in parent
    // Reset history
    setHistory([]);
    setHistoryIndex(-1);

    // Show success toast message
    toast.success(`Activity "${selectedActivity.label}" saved successfully`);
  };

  const handleEditSavedActivity = (activity: SavedActivity) => {
    // Find the activity in activityData to get the correct value
    const activityFromData = activityData.find((a:any) => a.name === activity.name);
    if (!activityFromData) return;

    onActivityChange({
      label: activity.name,
      value: activityFromData.data.parameters,
    });
    setZones(activity.zones);
    setActivityParameters(activity.parameters);
    setIsDrawingEnabled(false);
    setEditingZoneIndex(null);
    setEditingActivityId(activity.id);
    setActivitySaved(false);
    setShowZonesSection(true);

    // Initialize history with loaded activity state
    setHistory([
      {
        zones: activity.zones,
        activityParameters: activity.parameters,
      },
    ]);
    setHistoryIndex(0);

    // Scroll to parameters area instead of drawing area
    scrollToParametersArea();
  };

  const handleDeleteSavedActivity = (id: string) => {
    setSavedActivities(
      savedActivities.filter((activity) => activity.id !== id)
    );
  };

  const handlePolygonHover = (index: number | null) => {
    setHoveredZoneIndex(index);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const { mutate: updateConfig } = useMutation(
    putRequest,
    optimisticOptions(queries.config)
  );

  const saveConfig = useCallback(
    (e: any) => {
      e.preventDefault();

      let activities_data: Record<string, ActivityData> = {};
      savedActivities.forEach((activity) => {
        // Convert zones array to object with keys like 'zone1', 'zone2', etc.
        const zonesObject: { [key: string]: number[][] } = {};
        activity.zones.forEach((zone, index) => {
          // Convert Point[] to number[][] by mapping each Point to [x, y]
          zonesObject[zone.name] = zone.points.map((point) => [
            point.x,
            point.y,
          ]);
        });

        activities_data[activity.name] = {
          zones: zonesObject,
          parameters: activity.parameters,
        };
      });

      updateConfig({
        endPoint: `/config/${uin_id}`,
        payload: {
          activities_data: activities_data,
          uin_id: uin_id,
          client_id: data.client_id,
        },
      });

      // Show success toast message
      toast.success("Configuration saved successfully");

      navigate(-1);
    },
    [uin_id, savedActivities]
  );

  const { data: snapshotData = {} }: any = useQuery(
    [queries.snapshot, uin_id],
    () => {
      return getRequest(`/config/get-zone-snapshot/${uin_id}`);
    },
    {
      enabled: !!uin_id,
      onSettled: () => {
        // Simulate loading for a better user experience
        setTimeout(() => {
          setIsPageLoading(false);
        }, 3000);
      }
    }
  );
  const { data: zone = {} } = snapshotData;

  const { mutate: requestZoneSnapshot } = useMutation(
    postRequest
  );

  const handleRequestZoneSnapshot = () => {
    // Show loading toast
    toast.info("Requesting new snapshot. This may take a few moments...");
    
    requestZoneSnapshot({
      endPoint: `/config/request-zone-snapshot`,
      payload: { uin_id: uin_id },
    });
    
    // Set loading state to true
    setIsSnapshotLoading(true);
    // Add a delay before refetching the snapshot URL
    setTimeout(() => {
      queryClient.invalidateQueries([queries.snapshot, uin_id]);
      // Set loading state to false after the snapshot is loaded
      setTimeout(() => {
        setIsSnapshotLoading(false);
      }, 4000); // Extra delay to ensure image is loaded
    }, 8000); // Increased from 9000 to 12000 ms to give more time for the snapshot to be taken and uploaded
  };

  // Add keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Load image and set canvas size
  useEffect(() => {
    if (zone && zone.snapshot) {
      const img = new window.Image();
      img.onload = () => {
        setImageWidth(img.naturalWidth);
        setImageHeight(img.naturalHeight);
      };
      img.src = zone.snapshot;
    } else {
      setImageWidth(null);
      setImageHeight(null);
    }
  }, [zone && zone.snapshot]);

  const scale = 0.8;
  const scaledWidth = imageWidth ? Math.round(imageWidth * scale) : undefined;
  const scaledHeight = imageHeight ? Math.round(imageHeight * scale) : undefined;

  useEffect(() => {
    if (config.uin_id) {
      // Convert activities_data object into an array format
      const activitiesArray = Object.entries(config.activities_data || {}).map(([name, data]) => ({
        name,
        data
      }));
      setActivityData(activitiesArray);
    }
  }, [config]);

  if (isConfigLoading) {
    return <div>Loading...</div>;
  }


  return (
    <div className="p-2 md:p-4 lg:p-6 bg-gray-50 min-h-screen">
      {/* Page Loading Overlay */}
      {isPageLoading && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-white flex flex-col items-center justify-center z-50"
        >
          <div className="relative flex flex-col items-center">
            <div className="w-20 h-20">
              <svg className="animate-spin w-20 h-20 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                <>{FaDrawPolygon({ size: 20, color: "#2563eb" })}</>
              </div>
            </div>
          </div>
          <motion.div 
            className="mt-6 text-center"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-xl font-bold text-gray-800 mb-1">Loading Zone Configuration</h3>
    
          </motion.div>
          <motion.div 
            className="mt-6 w-64 h-1.5 bg-gray-200 rounded-full overflow-hidden"
            initial={{ width: 0 }}
            animate={{ width: "16rem" }}
            transition={{ duration: 0.4 }}
          >
            <motion.div 
              className="h-full bg-blue-600 rounded-full"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
            />
          </motion.div>
        </motion.div>
      )}

      {/* Zone Configuration */}
      <div className="mb-4 md:mb-6 flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0">
        <div className="w-full md:w-auto">
          <h2 className="text-xl md:text-2xl font-bold mb-1 md:mb-2 text-gray-800">
            Zone Configuration
          </h2>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl">
            {activitySaved && !showZonesSection
              ? "Activity saved successfully. You can select another activity or edit a saved activity."
              : typeof editingZoneIndex === 'number' && zones[editingZoneIndex]
              ? `Editing "${zones[editingZoneIndex].name}". Click to add points in sequence. Drag existing points to adjust position. To complete, click near the starting point.`
              : isDrawingEnabled
              ? `Drawing zone "${newZoneName}". Click points on the canvas to draw. Click near the starting point to complete.`
              : selectedActivity && zones.length === 0
              ? 'No zones created yet. Click "Draw New Zone" to start creating a zone.'
              : selectedActivity
              ? `Created ${zones.length} zone${zones.length !== 1 ? "s" : ""}. You can create more zones or save the activity.`
              : "Please select an activity to start drawing zones."}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch md:items-center gap-2 md:gap-3 mt-3 md:mt-0 w-full md:w-auto">
          <div className="w-full sm:w-64 flex items-center">
            <Select
              inputId="activity-select-header"
              value={selectedActivity}
              onChange={handleActivityChange}
              options={activityData ? activityData.map((ele:any) => ({
                label: ele.name,
                value: ele.data.parameters,
              })) : []}
              className="w-full"
              placeholder="Select Activity"
              isClearable
              isSearchable
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: "#d1d5db",
                  boxShadow: "none",
                  borderRadius: "0.5rem",
                  minHeight: "38px",
                  fontSize: "0.875rem",
                }),
                placeholder: (base) => ({
                  ...base,
                  color: "#9ca3af",
                  fontSize: "0.875rem",
                }),
              }}
            />
          </div>
          {showZonesSection && (
            <>
              <button
                onClick={handleNewZoneClick}
                disabled={!selectedActivity || isDrawingEnabled}
                className={`w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-lg transition-all shadow-sm
                  ${
                    isDrawingEnabled
                      ? "bg-blue-600 text-white opacity-60 cursor-not-allowed"
                      : !selectedActivity
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }
                `}
              >
                {isDrawingEnabled ? "Drawing..." : "Draw New Zone"}
              </button>
            </>
          )}
          {showZonesSection && selectedActivity && zones.length > 0 && (
            <button
              onClick={handleSaveActivity}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              Save Activity
            </button>
          )}
        </div>
      </div>

      {/* Responsive main content: canvas + controls */}
      <div
        ref={drawingAreaRef}
        className={`flex flex-col lg:flex-row gap-4 md:gap-6 transition-all duration-500 ${
          highlightDrawingArea ? "ring-4 ring-blue-400 rounded-xl p-2" : ""
        }`}
      >
        <div className="flex-1 bg-white rounded-xl shadow-sm overflow-hidden w-full max-w-full">
          {/* Canvas header with all action buttons */}
          <div className="w-full flex items-center justify-center gap-x-6 py-2 bg-white border-b border-gray-200">
            <Tooltip msg="Undo">
              <button
                onClick={handleUndo}
                disabled={!undoRedoEnabled || historyIndex <= 0}
                className={`p-2 text-lg rounded-lg shadow-sm flex items-center justify-center
                  ${!undoRedoEnabled || historyIndex <= 0 
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                  }
                `}
                aria-label="Undo"
              >
                <>{FaUndo({})}</>
              </button>
            </Tooltip>
            <Tooltip msg="Redo">
              <button
                onClick={handleRedo}
                disabled={!undoRedoEnabled || historyIndex >= history.length - 1}
                className={`p-2 text-lg rounded-lg shadow-sm flex items-center justify-center
                  ${!undoRedoEnabled || historyIndex >= history.length - 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                  }
                `}
                aria-label="Redo"
              >
                <>{FaRedo({})}</>
              </button>
            </Tooltip>
            <Tooltip msg="Take Latest Snapshot">
              <button
                ref={controlButtonRef}
                onClick={handleRequestZoneSnapshot}
                className="bg-white p-2 sm:p-3 rounded-xl shadow-sm transition-all duration-200 border border-gray-200"
                aria-label="Take Latest Snapshot"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="sm:w-5 sm:h-5"
                >
                  <path
                    d="M4 12C4 7.58172 7.58172 4 12 4C15.3 4 18.1 5.9 19.2 8.7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="text-blue-600"
                  />
                  <path
                    d="M20 12C20 16.4183 16.4183 20 12 20C8.7 20 5.9 18.1 4.8 15.3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="text-blue-600"
                  />
                  <path
                    d="M20 4V8H16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-blue-600"
                  />
                  <path
                    d="M4 20V16H8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-blue-600"
                  />
                </svg>
              </button>
            </Tooltip>
            <Tooltip msg="Drawing Controls">
              <button
                ref={controlButtonRef}
                onClick={toggleControls}
                className="bg-white p-2 sm:p-3 rounded-xl shadow-sm transition-all duration-200 border border-gray-200"
                aria-label="Drawing Controls"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="sm:w-5 sm:h-5"
                >
                  <path
                    d="M3 5.5V14.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="text-blue-600"
                  />
                  <path
                    d="M10 2V18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="text-blue-600"
                  />
                  <path
                    d="M17 5.5V14.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="text-blue-600"
                  />
                  <circle cx="3" cy="10" r="2" className="fill-blue-600" />
                  <circle cx="10" cy="6" r="2" className="fill-blue-600" />
                  <circle cx="17" cy="10" r="2" className="fill-blue-600" />
                </svg>
              </button>
            </Tooltip>
          </div>
          <div className="w-full flex items-center justify-center" style={scaledWidth && scaledHeight ? { width: scaledWidth, height: scaledHeight } : {}}>
            {showControls && showZonesSection && (
              <div
                className="absolute top-40 right-2 sm:top-52 sm:right-8 z-50 w-[90vw] max-w-xs sm:w-80"
                style={{
                  maxHeight: scaledHeight ? scaledHeight - 32 : undefined,
                  overflowY: 'auto',
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className="bg-white rounded-md shadow-lg w-full overflow-hidden border border-gray-200"
                >
                  <div ref={controlsRef}>
                    <DrawingControls
                      strokeColor={strokeColor}
                      setStrokeColor={setStrokeColor}
                      pointRadius={pointRadius}
                      setPointRadius={setPointRadius}
                      strokeWidth={strokeWidth}
                      setStrokeWidth={setStrokeWidth}
                      fillOpacity={fillOpacity}
                      setFillOpacity={setFillOpacity}
                      onClose={toggleControls}
                    />
                  </div>
                </motion.div>
              </div>
            )}
            <div
              className="relative"
              onMouseMove={handleMouseMove}
              style={scaledWidth && scaledHeight ? { width: scaledWidth, height: scaledHeight } : {}}
            >
              {scaledWidth && scaledHeight && (
                <Drawer
                  width={scaledWidth}
                  height={scaledHeight}
                  snapshot={zone.snapshot}
                  onPolygonComplete={handlePolygonComplete}
                  onPolygonUpdate={handlePolygonUpdate}
                  onPolygonSelected={handleEditZone}
                  existingPolygons={zones.map((zone) => zone.points)}
                  editingPolygonIndex={editingZoneIndex}
                  className="w-full h-full"
                  strokeColor={strokeColor}
                  fillColor={strokeColor}
                  pointRadius={pointRadius}
                  strokeWidth={strokeWidth}
                  fillOpacity={fillOpacity}
                  isDrawingEnabled={isDrawingEnabled && showZonesSection}
                  zoneNames={zones.map((zone) => zone.name)}
                  onPolygonHover={handlePolygonHover}
                  drawingPoints={isDrawingEnabled && editingZoneIndex === null ? drawingPoints : undefined}
                  onDrawingPointsChange={isDrawingEnabled && editingZoneIndex === null ? handleDrawingPointsChange : undefined}
                />
              )}
              {hoveredZoneIndex !== null && zones[hoveredZoneIndex] && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className="fixed bg-white px-3 py-1.5 rounded-lg shadow-md border border-gray-200 text-sm font-medium text-gray-800 z-40 pointer-events-none flex items-center gap-2"
                  style={{
                    left: mousePosition.x + 15,
                    top: mousePosition.y + 15,
                    transform: "translate(0, 0)",
                  }}
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: strokeColor }}
                  />
                  {zones[hoveredZoneIndex].name}
                </motion.div>
              )}
              
              {/* Loading overlay */}
              {isSnapshotLoading && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="fixed inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center z-50"
                >
                  <div className="relative">
                    <div className="w-16 h-16">
                      <svg className="animate-spin w-16 h-16 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                        <>{FaDrawPolygon({ size: 16, color: "#2563eb" })}</>
                      </div>
                    </div>
                  </div>
                  <motion.p 
                    className="mt-4 text-gray-700 font-medium text-center max-w-xs"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    Capturing latest snapshot
                  </motion.p>
                  <motion.div 
                    className="mt-3 w-48 h-1.5 bg-gray-200 rounded-full overflow-hidden"
                    initial={{ width: 0 }}
                    animate={{ width: "12rem" }}
                    transition={{ duration: 0.5 }}
                  >
                    <motion.div 
                      className="h-full bg-blue-600 rounded-full"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 12, ease: "linear" }}
                    />
                  </motion.div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status message when editing a zone */}
      {showZonesSection && typeof editingZoneIndex === 'number' && zones[editingZoneIndex] && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-400"></div>
          <span>
            Editing zone: <strong>{zones[editingZoneIndex].name}</strong>. Click
            anywhere to add points in sequence. Double-click points to remove
            them. To complete the shape and finish editing, click on the
            starting point (highlighted when you hover near it).
          </span>
        </div>
      )}

      {/* Activity Parameters */}
      {selectedActivity && !activitySaved && showZonesSection && (
        <div ref={parametersRef}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 md:mb-8 mt-6 md:mt-8 bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8 transition-all duration-500 ${
              highlightParameters ? "ring-4 ring-blue-400" : ""
            }`}
          >
            <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-gray-800 flex items-center">
              Activity Parameters
              {editingActivityId && (
                <span className="ml-3 text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                  Editing
                </span>
              )}
            </h2>
            {editingActivityId && (
              <p className="text-gray-600 text-sm mb-4">
                Update the parameters below, then you can draw or edit zones in
                the canvas above.
              </p>
            )}
            <div className="max-w-full overflow-x-auto">
              <div className="min-w-[280px] md:min-w-0">
                <ParametersForm
                  selectedActivity={selectedActivity.value}
                  parameters={activityParameters}
                  onChange={handleParametersChange}
                />
              </div>
            </div>
            {editingActivityId && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={scrollToDrawingArea}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Continue to Zone Drawing
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Name Dialog */}
      {showNameDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl p-6 md:p-8 w-full max-w-xs md:max-w-md shadow-2xl"
          >
            <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-gray-800">
              New Zone
            </h3>
            <form onSubmit={handleNameSubmit}>
              <label
                htmlFor="zoneName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Zone Name:
              </label>
              <input
                id="zoneName"
                type="text"
                value={newZoneName}
                onChange={(e) => setNewZoneName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Enter zone name"
                autoFocus
                required
              />
              <div className="mt-6 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowNameDialog(false);
                    setNewZoneName("");
                  }}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Start Drawing
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Saved Activities Table */}
      {savedActivities.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 md:mt-12 bg-white rounded-xl shadow-sm p-4 md:p-6 mb-8"
        >
          <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-gray-800">
            Saved Activities
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm md:text-base">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity Name
                  </th>
                  <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zones
                  </th>
                  <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {savedActivities.map((activity) => (
                  <React.Fragment key={activity.id}>
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <div className="text-sm md:text-base font-medium text-gray-900">
                          {activity.name}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {activity.zones.length} zones
                          </span>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            onClick={() => handleEditSavedActivity(activity)}
                            className="w-full sm:w-auto px-3 py-1.5 text-sm font-medium bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteSavedActivity(activity.id)
                            }
                            className="w-full sm:w-auto px-3 py-1.5 text-sm font-medium bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Save Configuration Button */}
      {((selectedActivity && zones.length > 0) || activitySaved) && (
        <div className="mt-4 mb-8 flex justify-center">
          <button
            onClick={saveConfig}
            className="px-6 py-3 text-base font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-md"
          >
            Save Configuration
          </button>
        </div>
      )}
    </div>
  );
};
